import React from 'react';
import { useNavigate } from 'react-router-dom';

import { isAuthError } from '../../api/client';
import type { GuestAccessRuleDto } from '../../api/guestAccessTypes';
import { canAccessAdmin } from '../../domain/auth/role';
import { guestAccessErrorMessage } from '../../domain/guestAccess/errors';
import { formatRuleDay, formatRuleTime, isRuleFinished } from '../../domain/guestAccess/guestAccess';
import { getResourceLabel } from '../../resources/resources';
import { guestAccessService } from '../../services/singletons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions } from '../../store/slices/uiSlice';
import { TavernButton, TavernEmptyState, TavernLoading } from '../../ui/components';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import './GuestAccessPage.css';

const ROOM_BY_RESOURCE: Record<string, string> = {
  table_game_1: 'Таверна',
  table_game_2: 'Таверна',
  table_game_3: 'Таверна',
  table_game_4: 'Двор',
  table_game_5: 'VIP',
  table_game_6: 'VIP',
  table_game_7: 'VIP',
};

function GuestAccessCard({
  rule,
  finished,
  onEdit,
  onDelete,
}: {
  rule: GuestAccessRuleDto;
  finished: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={`guest-access-card${finished ? ' guest-access-card--finished' : ''}`}>
      <div className="guest-access-card__head">
        <div>
          <div className="guest-access-card__day">{formatRuleDay(rule.startAt)}</div>
          <div className="guest-access-card__time">🕐 {formatRuleTime(rule)}</div>
        </div>
        <div className="guest-access-card__actions">
          {!finished ? (
            <button type="button" className="guest-access-icon" aria-label="Редактировать" onClick={onEdit}>
              ✏️
            </button>
          ) : null}
          <button type="button" className="guest-access-icon" aria-label="Удалить" onClick={onDelete}>
            🗑️
          </button>
        </div>
      </div>

      <div className="guest-access-card__tables">
        {rule.resourceIds.map((id) => (
          <span key={id} className="guest-access-chip">
            {getResourceLabel(id)}
            <small>{ROOM_BY_RESOURCE[id] ?? ''}</small>
          </span>
        ))}
      </div>

      <div className="guest-access-card__meta">
        <span>Мероприятий от гостей: {rule.guestEventsCount}</span>
        {rule.notifyTelegram ? <span>🔔 Telegram</span> : null}
      </div>
    </div>
  );
}

export function GuestAccessPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const role = useAppSelector((s) => s.auth.user?.role);
  const canAccess = canAccessAdmin(role);

  const [rules, setRules] = React.useState<GuestAccessRuleDto[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<GuestAccessRuleDto | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    try {
      setRules(await guestAccessService.listRules());
    } catch (error) {
      if (!isAuthError(error)) {
        dispatch(
          uiActions.setNotification({
            type: 'error',
            message: guestAccessErrorMessage(error, 'Не удалось загрузить гостевой доступ'),
          }),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  React.useEffect(() => {
    if (canAccess) void load();
  }, [canAccess, load]);

  const confirmDelete = React.useCallback(async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setIsDeleting(true);
    try {
      await guestAccessService.deleteRule(target.id);
      setPendingDelete(null);
      dispatch(uiActions.setNotification({ type: 'success', message: 'Гостевой доступ удалён' }));
      await load();
    } catch (error) {
      if (!isAuthError(error)) {
        dispatch(
          uiActions.setNotification({
            type: 'error',
            message: guestAccessErrorMessage(error, 'Не удалось удалить гостевой доступ'),
          }),
        );
      }
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, load, pendingDelete]);

  const { upcoming, archive } = React.useMemo(() => {
    const now = new Date();
    return {
      upcoming: rules.filter((rule) => !isRuleFinished(rule, now)),
      archive: rules.filter((rule) => isRuleFinished(rule, now)).reverse(),
    };
  }, [rules]);

  if (!canAccess) {
    return (
      <div className="ttbPage guest-access-page">
        <TavernEmptyState icon="🔒" title="Нет доступа" description="Раздел доступен только администраторам" />
      </div>
    );
  }

  return (
    <div className="ttbPage guest-access-page">
      <ConfirmSheet
        open={pendingDelete != null}
        title="Удалить гостевой доступ?"
        message="Новые мероприятия по этому правилу создавать будет нельзя. Уже созданные гостями мероприятия останутся."
        confirmLabel={isDeleting ? 'Удаление…' : 'Удалить'}
        destructive
        onCancel={() => {
          if (!isDeleting) setPendingDelete(null);
        }}
        onConfirm={() => void confirmDelete()}
      />

      <div className="guest-access-header">
        <button type="button" className="guest-access-back" onClick={() => navigate('/profile')}>
          ← Назад
        </button>
        <h1 className="ttbPageTitle">
          <span>🟢</span>
          Гостевой доступ
        </h1>
        <TavernButton variant="primary" onClick={() => navigate('/guests/new')}>
          ＋ Создать
        </TavernButton>
      </div>

      <div className="ttbPageContent guest-access-content">
        {isLoading && rules.length === 0 ? <TavernLoading /> : null}

        {!isLoading && rules.length === 0 ? (
          <TavernEmptyState
            icon="🎲"
            title="Гостевой доступ не настроен"
            description="Откройте столы, чтобы гости могли создавать мероприятия"
          />
        ) : null}

        {upcoming.map((rule) => (
          <GuestAccessCard
            key={rule.id}
            rule={rule}
            finished={false}
            onEdit={() => navigate(`/guests/${rule.id}/edit`)}
            onDelete={() => setPendingDelete(rule)}
          />
        ))}

        {archive.length > 0 ? (
          <>
            <div className="guest-access-section-title">Архив</div>
            {archive.map((rule) => (
              <GuestAccessCard key={rule.id} rule={rule} finished onEdit={() => undefined} onDelete={() => setPendingDelete(rule)} />
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
}
